<script lang="ts" setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  shallowRef,
  useSlots,
  watch,
  type Component,
  type VNodeChild,
} from 'vue';
import { autoUpdate, flip, offset, shift, useFloating } from '@floating-ui/vue';
import { IconClose, IconMinus, IconPlus } from '@/icons';
import SkeletonRectangle from '@/app/components/skeleton-rectangle/skeleton-rectangle.vue';
import placeholderImage from '@/app/components/product-image/placeholder.png';
import { replaceBrokenImageSrc } from '@/shared/utils';
import { hasSlotContent } from '../../utils';
import { UiButton } from '../ui-button';
import { UiButtonIcon } from '../ui-button-icon';
import { UiCheckbox } from '../ui-checkbox';
import { ObPlaceholderImage } from '../placeholder-image';
import { UiTag } from '../ui-tag';
import { UiTableContextPanel, UiTableContextPanelItem } from '../ui-table-context-panel';
import type { ProductCardKpiItem, ProductCardTag } from './interfaces';
interface Props {
  title: string;
  subtitle?: string;
  image?: string;
  tags?: (ProductCardTag | string)[];
  maxTags?: number;
  topTag?: string;
  topTagIcon?: Component;
  selectable?: boolean;
  selected?: boolean;
  contextPanelVisible?: boolean;
  progressLabel?: string;
  progressValue?: number;
  progressMax?: number;
  kpis?: ProductCardKpiItem[];
  editableKpis?: boolean;
  kpiApplyLabel?: string;
  rejectLabel?: string;
  rejectIcon?: Component;
  rejectActive?: boolean;
  approveLabel?: string;
  approveIcon?: Component;
  approveActive?: boolean;
  approveFilled?: boolean;
  loadingKpis?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  selectable: false,
  selected: false,
  contextPanelVisible: false,
  rejectActive: false,
  approveActive: false,
  approveFilled: false,
  editableKpis: false,
  kpiApplyLabel: 'Apply',
  loadingKpis: false,
});
const emit = defineEmits<{
  reject: [];
  approve: [];
  'update:selected': [value: boolean];
  'kpi-apply': [index: number, value: number];
}>();
const slots = useSlots();
defineSlots<{
  default?: () => VNodeChild;
  contextActions?: () => VNodeChild;
  kpi?: (props: {
    kpi: ProductCardKpiItem;
    index: number;
    titleClass: string;
    valueClass: string;
  }) => VNodeChild;
}>();
const hasButtons = computed(() => props.rejectLabel || props.approveLabel);
const hasTags = computed(() => props.tags && props.tags.length > 0);
const hasContextPanel = computed(() => props.selectable || hasSlotContent(slots.contextActions));
const imageLoadFailed = ref(false);
const hasHeaderImage = computed(() => Boolean(props.image) && !imageLoadFailed.value);
const hasProgress = computed(
  () => props.progressLabel && props.progressValue != null && props.progressMax != null,
);
const progressBarWidth = computed(() =>
  props.progressMax ? `${(props.progressValue! / props.progressMax) * 100}%` : '0%',
);
const normalizedTags = computed(() => {
  if (!props.tags) return [];
  return props.tags.map((t) => (typeof t === 'string' ? { label: t } : t));
});
const visibleTags = computed(() => {
  if (props.maxTags == null) return normalizedTags.value;
  return normalizedTags.value.slice(0, props.maxTags);
});
const overflowCount = computed(() => {
  if (props.maxTags == null) return 0;
  return Math.max(normalizedTags.value.length - props.maxTags, 0);
});
const overflowTags = computed(() => {
  if (props.maxTags == null) return [];
  return normalizedTags.value.slice(props.maxTags);
});
const overflowTagRef = shallowRef<HTMLElement | null>(null);
const overflowPopoverRef = shallowRef<HTMLElement | null>(null);
const overflowPopoverOpen = ref(false);
const { floatingStyles: overflowPopoverStyles } = useFloating(overflowTagRef, overflowPopoverRef, {
  placement: 'bottom-start',
  middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
  open: overflowPopoverOpen,
  transform: false,
});
function onCheckboxChange(val: unknown) {
  emit('update:selected', !!val);
}
function onHeaderImageError(event: Event) {
  replaceBrokenImageSrc(event, placeholderImage);
  imageLoadFailed.value = true;
}
watch(
  () => props.image,
  () => {
    imageLoadFailed.value = false;
  },
);
function isKpiEditable(kpi: ProductCardKpiItem) {
  return props.editableKpis && !props.loadingKpis && kpi.editable !== false;
}
function onKpiKeydown(event: KeyboardEvent, index: number, kpi: ProductCardKpiItem) {
  if (!isKpiEditable(kpi)) {
    return;
  }
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    openKpiEdit(index, kpi);
  }
}
function resolveKpiNumericValue(kpi: ProductCardKpiItem) {
  if (typeof kpi.value === 'number' && Number.isFinite(kpi.value)) {
    return Math.max(0, Math.round(kpi.value));
  }
  const parsed = Number.parseFloat(String(kpi.value));
  if (!Number.isFinite(parsed)) {
    return 0;
  }
  return Math.max(0, Math.round(parsed));
}
const openKpiIndex = ref<number | null>(null);
const kpiEditValue = ref(0);
const kpiInputRef = ref<HTMLInputElement | null>(null);
const kpiRefs = ref<(HTMLElement | null)[]>([]);
const kpiPopoverRef = shallowRef<HTMLElement | null>(null);
const valueEditorInstanceId = `ui-product-card-kpi-editor-${Math.random().toString(36).slice(2)}`;
const activeKpiRef = computed(() => {
  if (openKpiIndex.value == null) return null;
  return kpiRefs.value[openKpiIndex.value] ?? null;
});
const { floatingStyles: kpiPopoverStyles } = useFloating(activeKpiRef, kpiPopoverRef, {
  placement: 'right-start',
  middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  whileElementsMounted: autoUpdate,
  open: computed(() => openKpiIndex.value != null),
  transform: false,
});
function onDocumentPointerDown(event: PointerEvent) {
  if (openKpiIndex.value == null) {
    return;
  }
  const target = event.target as HTMLElement | null;
  if (!target) {
    return;
  }
  if (kpiPopoverRef.value?.contains(target)) {
    return;
  }
  const clickedKpiCard = target.closest('[data-kpi-card]') as HTMLElement | null;
  const isOwnKpiCard =
    clickedKpiCard != null && kpiRefs.value.some((refEl) => refEl === clickedKpiCard);
  if (isOwnKpiCard) {
    return;
  }
  closeKpiEdit();
}
function onGlobalValueEditorOpen(event: Event) {
  const detail = (
    event as CustomEvent<{
      sourceId?: string;
    }>
  ).detail;
  if (detail?.sourceId === valueEditorInstanceId) {
    return;
  }
  closeKpiEdit();
}
onMounted(() => {
  window.addEventListener(
    'ui-product-card-value-editor-open',
    onGlobalValueEditorOpen as EventListener,
  );
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
});
onBeforeUnmount(() => {
  window.removeEventListener(
    'ui-product-card-value-editor-open',
    onGlobalValueEditorOpen as EventListener,
  );
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
});
function setKpiRef(index: number) {
  return (el: unknown) => {
    kpiRefs.value[index] = (el as HTMLElement | null) ?? null;
  };
}
function openKpiEdit(index: number, kpi: ProductCardKpiItem) {
  if (!isKpiEditable(kpi)) {
    closeKpiEdit();
    return;
  }
  window.dispatchEvent(
    new CustomEvent('ui-product-card-value-editor-open', {
      detail: { sourceId: valueEditorInstanceId },
    }),
  );
  kpiEditValue.value = resolveKpiNumericValue(kpi);
  openKpiIndex.value = index;
  nextTick(() => kpiInputRef.value?.select());
}
function closeKpiEdit() {
  openKpiIndex.value = null;
}
function decrementKpiEdit() {
  if (kpiEditValue.value > 0) {
    kpiEditValue.value -= 1;
  }
}
function incrementKpiEdit() {
  kpiEditValue.value += 1;
}
function applyKpiEdit() {
  if (openKpiIndex.value == null) {
    return;
  }
  const nextValue = Math.max(0, Math.round(kpiEditValue.value || 0));
  emit('kpi-apply', openKpiIndex.value, nextValue);
  closeKpiEdit();
}
function onKpiInputKeydown(event: KeyboardEvent) {
  if (event.key === 'Enter') {
    event.preventDefault();
    applyKpiEdit();
  }
  if (event.key === 'Escape') {
    event.preventDefault();
    closeKpiEdit();
  }
}
</script>

<template>
  <div
    :class="[$style.root, { [$style.contextPanelVisible]: props.contextPanelVisible }]"
    data-ui-component="UiProductCard"
  >
    <UiTableContextPanel v-if="hasContextPanel" :class="$style.contextPanel">
      <slot name="contextActions" />
      <UiTableContextPanelItem v-if="props.selectable" :class="$style.contextCheckboxItem">
        <UiCheckbox :model-value="props.selected" @update:model-value="onCheckboxChange" />
      </UiTableContextPanelItem>
    </UiTableContextPanel>

    <div v-if="props.topTag" :class="$style.topTag">
      <component :is="props.topTagIcon" v-if="props.topTagIcon" :class="$style.topTagIcon" />
      {{ props.topTag }}
    </div>

    <div :class="$style.header">
      <div v-if="hasHeaderImage" :class="$style.imageWrap">
        <img
          :src="props.image"
          :alt="props.title"
          :class="$style.image"
          @error="onHeaderImageError"
        />
      </div>
      <div v-else-if="props.image" :class="$style.imageWrap">
        <ObPlaceholderImage :source="placeholderImage" :padded="false" />
      </div>
      <div :class="$style.headContent">
        <div :class="$style.headText">
          <div :class="$style.title">{{ props.title }}</div>
          <div v-if="props.subtitle" :class="$style.subtitle">
            {{ props.subtitle }}
          </div>
        </div>
        <div v-if="hasTags" :class="$style.tagRow">
          <template v-for="(tag, idx) in visibleTags" :key="idx">
            <button
              v-if="tag.clickable"
              type="button"
              :class="[$style.tagButton, $style.tagButtonInteractive]"
              :data-testid="tag.testId"
              @click="tag.onClick?.()"
            >
              <UiTag
                size="s"
                :variant="tag.variant"
                :text-color="tag.textColor"
                :background-color="tag.backgroundColor"
                :class="$style.tag"
              >
                <template v-if="tag.icon" #leadingVisual>
                  <component :is="tag.icon" />
                </template>
                {{ tag.label }}
              </UiTag>
            </button>
            <UiTag
              v-else
              size="s"
              :variant="tag.variant"
              :text-color="tag.textColor"
              :background-color="tag.backgroundColor"
              :class="$style.tag"
              :data-testid="tag.testId"
            >
              <template v-if="tag.icon" #leadingVisual>
                <component :is="tag.icon" />
              </template>
              {{ tag.label }}
            </UiTag>
          </template>
          <span
            v-if="overflowCount > 0"
            ref="overflowTagRef"
            :class="$style.overflowTagAnchor"
            @mouseenter="overflowPopoverOpen = true"
            @mouseleave="overflowPopoverOpen = false"
          >
            <UiTag
              size="s"
              text-color="var(--ui2-data-pine-forest)"
              background-color="var(--ui2-shade-pine-forest-16)"
            >
              +{{ overflowCount }}
            </UiTag>
          </span>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="overflowPopoverOpen && overflowTags.length"
        ref="overflowPopoverRef"
        :class="$style.overflowPopover"
        :style="overflowPopoverStyles"
        @mouseenter="overflowPopoverOpen = true"
        @mouseleave="overflowPopoverOpen = false"
      >
        <template v-for="(tag, idx) in overflowTags" :key="idx">
          <button
            v-if="tag.clickable"
            type="button"
            :class="[$style.tagButton, $style.tagButtonInteractive]"
            :data-testid="tag.testId"
            @click="tag.onClick?.()"
          >
            <UiTag
              size="s"
              :variant="tag.variant"
              :text-color="tag.textColor"
              :background-color="tag.backgroundColor"
              :class="$style.tag"
            >
              <template v-if="tag.icon" #leadingVisual>
                <component :is="tag.icon" />
              </template>
              {{ tag.label }}
            </UiTag>
          </button>
          <UiTag
            v-else
            size="s"
            :variant="tag.variant"
            :text-color="tag.textColor"
            :background-color="tag.backgroundColor"
            :class="$style.tag"
            :data-testid="tag.testId"
          >
            <template v-if="tag.icon" #leadingVisual>
              <component :is="tag.icon" />
            </template>
            {{ tag.label }}
          </UiTag>
        </template>
      </div>
    </Teleport>

    <div v-if="hasProgress" :class="$style.progress">
      <div :class="$style.progressLabel">{{ props.progressLabel }}</div>
      <div :class="$style.progressRow">
        <div :class="$style.progressTrack">
          <div :class="$style.progressFill" :style="{ width: progressBarWidth }" />
        </div>
        <span :class="$style.progressValue">{{ props.progressValue }}</span>
        <span :class="$style.progressMax">/{{ props.progressMax }}</span>
      </div>
    </div>

    <div v-if="props.kpis?.length" :class="$style.kpiGrid">
      <div :class="$style.kpiRow">
        <div
          v-for="(kpi, index) in props.kpis.slice(0, 2)"
          :key="index"
          :ref="setKpiRef(index)"
          :class="[
            $style.kpiCard,
            { [$style.kpiCardHighlighted]: kpi.highlighted },
            isKpiEditable(kpi) && $style.kpiCardEditable,
            isKpiEditable(kpi) && openKpiIndex === index && $style.kpiCardActive,
          ]"
          :role="isKpiEditable(kpi) ? 'button' : undefined"
          :tabindex="isKpiEditable(kpi) ? 0 : undefined"
          data-kpi-card
          :data-testid="`ui-product-card-kpi-${index}`"
          @click="openKpiEdit(index, kpi)"
          @keydown="onKpiKeydown($event, index, kpi)"
        >
          <template v-if="props.loadingKpis">
            <SkeletonRectangle :class="$style.kpiSkeleton" />
          </template>
          <template v-else>
            <slot
              name="kpi"
              :kpi="kpi"
              :index="index"
              :title-class="$style.kpiTitle"
              :value-class="$style.kpiValue"
            >
              <div :class="$style.kpiTitle">{{ kpi.title }}</div>
              <div :class="$style.kpiValue">{{ kpi.value }}</div>
            </slot>
          </template>
        </div>
      </div>
      <div v-if="props.kpis.length > 2" :class="$style.kpiRow">
        <div
          v-for="(kpi, index) in props.kpis.slice(2, 4)"
          :key="index"
          :ref="setKpiRef(index + 2)"
          :class="[
            $style.kpiCard,
            { [$style.kpiCardHighlighted]: kpi.highlighted },
            isKpiEditable(kpi) && $style.kpiCardEditable,
            isKpiEditable(kpi) && openKpiIndex === index + 2 && $style.kpiCardActive,
          ]"
          :role="isKpiEditable(kpi) ? 'button' : undefined"
          :tabindex="isKpiEditable(kpi) ? 0 : undefined"
          data-kpi-card
          :data-testid="`ui-product-card-kpi-${index + 2}`"
          @click="openKpiEdit(index + 2, kpi)"
          @keydown="onKpiKeydown($event, index + 2, kpi)"
        >
          <template v-if="props.loadingKpis">
            <SkeletonRectangle :class="$style.kpiSkeleton" />
          </template>
          <template v-else>
            <slot
              name="kpi"
              :kpi="kpi"
              :index="index + 2"
              :title-class="$style.kpiTitle"
              :value-class="$style.kpiValue"
            >
              <div :class="$style.kpiTitle">{{ kpi.title }}</div>
              <div :class="$style.kpiValue">{{ kpi.value }}</div>
            </slot>
          </template>
        </div>
      </div>
    </div>

    <Teleport to="body">
      <Transition
        :enter-from-class="$style.popoverEnterFrom"
        :enter-active-class="$style.popoverEnterActive"
        :leave-active-class="$style.popoverLeaveActive"
        :leave-to-class="$style.popoverLeaveTo"
      >
        <div
          v-if="openKpiIndex != null && !props.loadingKpis"
          ref="kpiPopoverRef"
          :class="$style.kpiPopover"
          :style="kpiPopoverStyles"
        >
          <div :class="$style.kpiPopoverInputRow">
            <input
              ref="kpiInputRef"
              v-model.number="kpiEditValue"
              type="number"
              :class="$style.kpiPopoverInput"
              @keydown="onKpiInputKeydown"
            />
            <UiButtonIcon variant="tertiary" size="s" @click="decrementKpiEdit">
              <IconMinus />
            </UiButtonIcon>
            <UiButtonIcon variant="tertiary" size="s" @click="incrementKpiEdit">
              <IconPlus />
            </UiButtonIcon>
          </div>
          <div :class="$style.kpiPopoverActionRow">
            <UiButtonIcon variant="secondary" @click="closeKpiEdit">
              <IconClose />
            </UiButtonIcon>
            <div :class="$style.kpiPopoverApplySlot">
              <UiButton variant="secondary" block @click="applyKpiEdit">
                {{ props.kpiApplyLabel }}
              </UiButton>
            </div>
          </div>
        </div>
      </Transition>
    </Teleport>

    <slot />

    <div v-if="hasButtons" :class="$style.actions">
      <div v-if="props.rejectLabel" :class="$style.actionSlot">
        <UiButton
          variant="secondary"
          :active="props.rejectActive"
          :class="{ [$style.rejectButtonActive]: props.rejectActive }"
          block
          @click="emit('reject')"
        >
          <template v-if="props.rejectIcon" #leadingVisual>
            <component :is="props.rejectIcon" />
          </template>
          {{ props.rejectLabel }}
        </UiButton>
      </div>
      <div v-if="props.approveLabel" :class="$style.actionSlot">
        <UiButton
          :variant="props.approveFilled ? 'primary' : 'secondary'"
          :active="props.approveActive"
          block
          @click="emit('approve')"
        >
          <template v-if="props.approveIcon" #leadingVisual>
            <component :is="props.approveIcon" />
          </template>
          {{ props.approveLabel }}
        </UiButton>
      </div>
    </div>
  </div>
</template>

<style lang="scss" module>
@use '../../styles/shared';
@use '../../styles/typography';

.root {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 16px;
  padding: 16px;
  width: 100%;
  box-sizing: border-box;
  font-family: typography.$font-family-primary;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-tertiary, #f0f1f4);
  border-radius: 12px;
  transition:
    box-shadow 0.15s ease,
    border-color 0.15s ease;

  &:hover {
    border-color: var(--ui2-stroke-secondary, #e4e5e8);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  }

  &:hover .contextPanel {
    opacity: 1;
    pointer-events: auto;
  }
}

.root.contextPanelVisible .contextPanel {
  opacity: 1;
  pointer-events: auto;
}

.contextPanel {
  position: absolute;
  top: 4px;
  right: 4px;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.15s ease;
  z-index: 1;
}

.contextCheckboxItem {
  :global(label[data-ui-component='UiCheckbox']) {
    margin: 0;
  }
}

.topTag {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  align-self: flex-start;
  padding: 2px 4px;
  border: 1px solid var(--ui2-stroke-primary, #d5d6d9);
  border-radius: 4px;
  @include typography.style('data-xs-medium');
  color: var(--ui2-content-primary, #010000);
}

.topTagIcon {
  width: 14px;
  height: 14px;
  flex: none;
}

.header {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  width: 100%;
}

.imageWrap {
  position: relative;
  width: 80px;
  height: 80px;
  border-radius: 4px;
  overflow: hidden;
  flex: none;
  background: var(--ui2-background-tone-5, #f5f5f5);

  &::after {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(167, 167, 167, 0.2);
    pointer-events: none;
  }
}

.image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.headContent {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 8px;
}

.headText {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.title {
  @include typography.style('data-s-medium');
  color: var(--ui2-content-primary, #010000);
}

.subtitle {
  @include typography.style('data-xs-regular');
  color: var(--ui2-content-secondary, #655e60);
}

.tagRow {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
  flex-wrap: wrap;
}

.tag {
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-primary, #d5d6d9);
}

.tagButton {
  @include shared.reset-button;
  display: inline-flex;
  flex: 0 1 auto;
  min-width: 0;
  max-width: 100%;
}

.tagButtonInteractive {
  cursor: pointer;
}

.tagButtonInteractive :global([data-ui-component='UiTag']) {
  border-color: transparent;
  transition: background-color 0.15s ease;
}

.tagButtonInteractive:hover :global([data-ui-component='UiTag']) {
  background: var(--ui2-shade-pine-forest-24, rgba(34, 107, 108, 0.24));
}

.progress {
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
}

.progressLabel {
  @include typography.style('data-xs-regular');
  color: var(--ui2-content-secondary, #655e60);
}

.progressRow {
  display: flex;
  align-items: center;
  gap: 5px;
  width: 100%;
}

.progressTrack {
  flex: 1;
  height: 4px;
  background: var(--ui2-shade-black-8);
  border-radius: 100px;
  overflow: hidden;
}

.progressFill {
  height: 100%;
  background: var(--ui2-data-pine-forest);
  border-radius: 100px;
  transition: width 0.3s ease;
}

.progressValue {
  @include typography.style('data-xs-mono');
  color: var(--ui2-content-primary, #010000);
}

.progressMax {
  @include typography.style('data-xs-mono');
  color: var(--ui2-content-secondary, #655e60);
}

.kpiGrid {
  display: flex;
  flex-direction: column;
  gap: 6px;
  width: 100%;
}

.kpiRow {
  display: flex;
  gap: 6px;
  width: 100%;
}

.kpiCard {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-height: 56px;
  padding: 8px;
  background: var(--ui2-shade-black-4);
  border-radius: 10px;
  overflow: hidden;
}

.kpiSkeleton {
  width: 100%;
  height: 42px;
  border-radius: 8px;
}

.kpiCardEditable {
  cursor: pointer;
}

.kpiCardEditable:focus-visible {
  outline: 1px solid var(--ui2-state-focus, #3355ff);
  outline-offset: 2px;
}

.kpiCardActive {
  outline: 1px solid var(--ui2-state-focus, #3355ff);
  outline-offset: -1px;
}

.kpiCardHighlighted {
  background: var(--ui2-shade-pine-forest-8);

  .kpiTitle {
    color: var(--ui2-data-pine-forest);
  }

  .kpiValue {
    color: var(--ui2-data-pine-forest);
  }
}

.kpiTitle {
  @include typography.style('data-2xs-medium');
  color: var(--ui2-content-tertiary, #978f91);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpiValue {
  @include typography.style('data-s-semibold');
  letter-spacing: -0.26px;
  color: var(--ui2-content-primary, #010000);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpiPopover {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 4px;
  width: 139px;
  box-sizing: border-box;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-secondary, #e4e5e8);
  border-radius: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.04);
  z-index: 1000;
}

.popoverEnterActive,
.popoverLeaveActive {
  transition:
    opacity 0.15s ease,
    transform 0.15s ease;
}

.popoverEnterFrom,
.popoverLeaveTo {
  opacity: 0;
  transform: scale(0.95);
}

.kpiPopoverInputRow {
  display: flex;
  align-items: center;
  gap: 4px;
  height: 32px;
  padding: 6px 6px 6px 8px;
  border: 1px solid var(--ui2-stroke-secondary, #e4e5e8);
  border-radius: 4px;
  box-sizing: border-box;
  transition: border-color 0.15s ease;

  &:focus-within {
    border-color: var(--ui2-state-focus, #3355ff);
  }
}

.kpiPopoverInput {
  flex: 1;
  min-width: 48px;
  border: none;
  outline: none;
  background: none;
  @include typography.style('data-s-mono');
  color: var(--ui2-content-primary, #010000);
  padding: 0;
  -moz-appearance: textfield;

  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
}

.kpiPopoverActionRow {
  display: flex;
  align-items: center;
  gap: 4px;
  width: 100%;
}

.kpiPopoverApplySlot {
  flex: 1;
  min-width: 0;
}

.actions {
  display: flex;
  gap: 8px;
  width: 100%;
  margin-top: auto;
}

.actionSlot {
  flex: 1;
  min-width: 0;

  :global([data-ui-component='UiButton']) {
    min-height: 28px;
    height: 28px;
    border-radius: 6px;
    padding: 0 8px;
  }
}

.rejectButtonActive {
  border-color: var(--ui2-state-error, #b2001b) !important;
  color: var(--ui2-state-error, #b2001b) !important;
  background: var(--ui2-state-error-shade, rgba(230, 57, 70, 0.12)) !important;
}

.overflowTagAnchor {
  display: inline-flex;
  align-items: center;
}

.overflowPopover {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 8px;
  max-width: 220px;
  background: var(--ui2-background-primary, #fff);
  border: 1px solid var(--ui2-stroke-secondary, #e4e5e8);
  border-radius: 8px;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.08);
  z-index: 1000;
}
</style>
